import React, { useState, useEffect, useCallback } from "react";
import { Button } from "./ui";
import { useLocation, useNavigate } from "react-router-dom";
import { MDBContainer, MDBRow, MDBCol, MDBCard, MDBCardBody, MDBCardTitle, MDBCardText, MDBTable, MDBTableHead, MDBTableBody, MDBIcon, MDBSpinner, MDBTooltip } from "mdb-react-ui-kit";
import NativeInput from "./NativeInput";
import { api } from "../services";
import { toast } from "react-toastify";
import { X, ChevronDown, RefreshCcw, Copy, Check } from "lucide-react";
import Actionbar from "./Actionbar";
import { useTranslation } from "react-i18next";
import { resolveTenantApiError } from "../utils/resolveTenantApiError";
import {
  Badge,
  Card,
  Col,
  ListGroup,
  Modal,
  Form,
  Row,
} from "react-bootstrap";

const DAYS_OF_WEEK = [
  "sunday",
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
];

function isValidTime(time) {
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(time);
}

function isStartBeforeEnd(start, end) {
  if (!isValidTime(start) || !isValidTime(end)) return false;
  return start < end;
}

// Parse time strings like "23:01", "11:01 pm" or "11:01pm" into minutes since midnight.
function parseTimeToMinutes(timeStr) {
  if (!timeStr || typeof timeStr !== "string") return NaN;
  const s = timeStr.trim().toLowerCase();

  // Match formats like "11:01 pm" or "11:01pm"
  const ampm = s.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/);
  if (ampm) {
    let h = Number(ampm[1]);
    const m = Number(ampm[2]);
    const period = ampm[3];
    if (period === "pm" && h !== 12) h += 12;
    if (period === "am" && h === 12) h = 0;
    return h * 60 + m;
  }

  // Match 24-hour format like "23:01"
  const match24 = s.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (match24) return Number(match24[1]) * 60 + Number(match24[2]);

  return NaN;
}

const ViewBranchPage = () => {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const { state } = useLocation();
  const navigate = useNavigate();
  const { storeId, branchId } = state || {};
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [tempBranch, setTempBranch] = useState(null);
  const [addingSlot, setAddingSlot] = useState(null); // Track which slot type is being added
  const [apps, setApps] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [branchRegister, setNewBranchRegistered] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [slotErrors, setSlotErrors] = useState({}); // { fieldName_index: { day: '', start: '', end: '' } }
  const [keetaShops, setKeetaShops] = useState([]);
  const [selectedKeetaShopId, setSelectedKeetaShopId] = useState("");
  const [keetaShopDetails, setKeetaShopDetails] = useState(null);
  const [keetaAvailable, setKeetaAvailable] = useState(false);
  const [foodicsShops, setFoodicsShops] = useState([]);
  const [selectedFoodicsShopId, setSelectedFoodicsShopId] = useState("");
  const [foodicsShopDetails, setFoodicsShopDetails] = useState(null);
  const [foodicsAvailable, setFoodicsAvailable] = useState(false);
  const [branches, setBranches] = useState([]);
  const [showEditAvailabilityModal, setShowEditAvailabilityModal] =
    useState(false);
  const [editAvailability, setEditAvailability] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [syncToggles, setSyncToggles] = useState({});
  const [togglingSync, setTogglingSync] = useState({});

  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const defaultAvailability = {
    monday: { is_visible: true, times: [{ start: "00:00", end: "23:59" }] },
    tuesday: { is_visible: true, times: [{ start: "00:00", end: "23:59" }] },
    wednesday: { is_visible: true, times: [{ start: "00:00", end: "23:59" }] },
    thursday: { is_visible: true, times: [{ start: "00:00", end: "23:59" }] },
    friday: { is_visible: true, times: [{ start: "00:00", end: "23:59" }] },
    saturday: { is_visible: true, times: [{ start: "00:00", end: "23:59" }] },
    sunday: { is_visible: true, times: [{ start: "00:00", end: "23:59" }] },
  };
  const [filteredKeetaShops, setFilteredKeetaShops] = useState([]);
  const [filteredFoodicsShops, setFilteredFoodicsShops] = useState([]);
  console.log("filterKeetaShops", filteredKeetaShops);
  console.log("filterFoodicsShops", filteredFoodicsShops);

  const keetaSync = Array.isArray(branch?.syncedApps)
    ? branch.syncedApps
        .filter((app) => app.app === "Keeta")
        .sort((a, b) => new Date(b.synced_date) - new Date(a.synced_date))[0]
    : null;

  const keetaExternalId = keetaSync?.external_id;

  const foodicsSync = Array.isArray(branch?.syncedApps)
    ? branch.syncedApps
        .filter((app) => app.app === "Foodics")
        .sort((a, b) => new Date(b.synced_date) - new Date(a.synced_date))[0]
    : null;

  const foodicsExternalId = foodicsSync?.external_id;

  const fetchKeetaAppDetails = useCallback(async () => {
    try {
      const response = await api.get(`/keeta-shops/${keetaExternalId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setKeetaShopDetails(response.data.data);
    } catch (error) {
      console.error("Error fetching keeta shop details:", error);
      // Clear keeta shop details if fetch fails
      setKeetaShopDetails(null);
    }
  }, [keetaExternalId]);

  const fetchFoodicsAppDetails = useCallback(async () => {
    try {
      const response = await api.get(`/foodics-shops/${foodicsExternalId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      setFoodicsShopDetails(response.data.data);
    } catch (error) {
      console.error("Error fetching foodics shop details:", error);
      // Clear foodics shop details if fetch fails
      setFoodicsShopDetails(null);
    }
  }, [foodicsExternalId]);
  const fetchStoreDetails = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${storeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranches(response.data.branches || []);
    } catch (error) {
      console.error(
        "Error fetching store details:",
        error.response?.data || error.message
      );
    }
  };

  useEffect(() => {
    if (keetaExternalId) {
      fetchKeetaAppDetails();
    } else {
      // Clear keeta shop details if no external ID
      setKeetaShopDetails(null);
    }
  }, [keetaExternalId, fetchKeetaAppDetails]);

  useEffect(() => {
    if (foodicsExternalId) {
      fetchFoodicsAppDetails();
    } else {
      // Clear foodics shop details if no external ID
      setFoodicsShopDetails(null);
    }
  }, [foodicsExternalId, fetchFoodicsAppDetails]);

  useEffect(() => {
    const fetchStoreApps = async () => {
      if (!storeId) return;

      try {
        const token = localStorage.getItem("token");

        const response = await api.get(`/stores/${storeId}/apps`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const apps = response.data?.apps || [];

        const keetaExists = apps.some(
          (app) => app.application?.toLowerCase() === "keeta"
        );

        const foodicsExists = apps.some(
          (app) => app.application?.toLowerCase() === "foodics"
        );

        setKeetaAvailable(keetaExists);
        setFoodicsAvailable(foodicsExists);
      } catch (err) {
        console.error("Error fetching store apps:", err);
      }
    };

    fetchStoreApps();
  }, [storeId]);

  const handleOpenEditAvailability = () => {
    if (
      tempBranch?.availableHours &&
      typeof tempBranch.availableHours === "object"
    ) {
      setEditAvailability(structuredClone(tempBranch.availableHours));
    } else {
      setEditAvailability({
        /* default structure for availableHours */
      });
    }
    setShowEditAvailabilityModal(true);
  };

  const [availabilityErrors, setAvailabilityErrors] = useState({});
  const handleAvailabilityChange = (day, field, value, idx = null) => {
    setEditAvailability((prev) => {
      const updated = { ...prev };
      let errorsCopy = { ...availabilityErrors };

      if (field === "is_visible") {
        updated[day].is_visible = value;

        // ✅ Add a default slot 08:00-09:00 if none exist when making visible
        if (
          value === true &&
          (!updated[day].times || updated[day].times.length === 0)
        ) {
          updated[day].times = [{ start: "08:00", end: "09:00" }];
        }
      } else if (field === "time") {
        const newTimes = [...updated[day].times];
        const updatedTime = { ...newTimes[idx], [value.key]: value.val };

        // Replace this slot with the new timing
        newTimes[idx] = updatedTime;
        updated[day].times = newTimes;

        // Clear previous errors for this day
        Object.keys(errorsCopy).forEach((key) => {
          if (key.startsWith(`${day}_`)) delete errorsCopy[key];
        });

        // Re-validate all slots for the day
        newTimes.forEach((slot, slotIdx) => {
          if (!slot.start || !slot.end) return;

          const slotStartMin = parseTimeToMinutes(slot.start);
          const slotEndMin = parseTimeToMinutes(slot.end);

          if (slotEndMin <= slotStartMin) {
            errorsCopy[`${day}_${slotIdx}`] = t("End time must be later than start time.");
            return;
          }

          const overlaps = newTimes.some((other, otherIdx) => {
            if (otherIdx === slotIdx || !other.start || !other.end) return false;

            const oStartMin = parseTimeToMinutes(other.start);
            const oEndMin = parseTimeToMinutes(other.end);

            return (
              (slotStartMin >= oStartMin && slotStartMin <= oEndMin) ||
              (slotEndMin >= oStartMin && slotEndMin <= oEndMin) ||
              (slotStartMin < oStartMin && slotEndMin > oEndMin) ||
              slotStartMin === oStartMin ||
              slotEndMin === oEndMin
            );
          });

          if (overlaps) {
            errorsCopy[`${day}_${slotIdx}`] = t("Time range overlaps with an existing slot.");
          }
        });
      }

      setAvailabilityErrors(errorsCopy);
      return updated;
    });
  };

  const handleAddTimeRange = (day) => {
    setEditAvailability((prev) => {
      const updated = { ...prev };
      const existingTimes = [...(updated[day]?.times || [])];

      // Merge all existing times (from state and tempBranch) INCLUDING current existingTimes
      const tempTimes = tempBranch?.availableHours?.[day]?.times || [];
      const allCurrentTimes = [...existingTimes, ...tempTimes]
        .filter((t) => t.start && t.end)
        .map((t) => {
          return {
            start: parseTimeToMinutes(t.start), // minutes since midnight
            end: parseTimeToMinutes(t.end),
          };
        })
        .sort((a, b) => a.start - b.start); // sort by start time

      const formatTime = (mins) => {
        // Ensure mins is a valid number
        if (isNaN(mins) || mins < 0) mins = 0;
        if (mins >= 24 * 60) mins = 24 * 60 - 1; // Cap at 23:59

        const h = Math.floor(mins / 60);
        const m = mins % 60;

        // Ensure proper formatting
        const hours = h.toString().padStart(2, "0");
        const minutes = m.toString().padStart(2, "0");

        return `${hours}:${minutes}`;
      };

      let newStart = null;
      let newEnd = null;
      let foundSlot = false;

      // Find the first available non-overlapping 1-hour slot
      for (let i = 0; i <= allCurrentTimes.length; i++) {
        let gapStart = i === 0 ? 0 : allCurrentTimes[i - 1].end;
        let gapEnd =
          i < allCurrentTimes.length ? allCurrentTimes[i].start : 24 * 60;

        // Check each possible 1-hour slot in this gap
        for (
          let slotStart = gapStart;
          slotStart + 60 <= gapEnd;
          slotStart += 60
        ) {
          const slotEnd = slotStart + 60;

          // Verify this slot doesn't conflict with any existing time
          const hasConflict = allCurrentTimes.some(
            (existing) =>
              (slotStart >= existing.start && slotStart < existing.end) ||
              (slotEnd > existing.start && slotEnd <= existing.end) ||
              (slotStart < existing.start && slotEnd > existing.end) ||
              slotStart === existing.end ||
              slotEnd === existing.start
          );

          if (!hasConflict) {
            newStart = slotStart;
            newEnd = slotEnd;
            foundSlot = true;
            break;
          }
        }

        if (foundSlot) break;
      }

      // If no gap found, force an overlapping slot
      if (!foundSlot) {
        newStart = 0; // 00:00
        newEnd = 60; // 01:00
      }

      // Add the slot
      existingTimes.push({
        start: formatTime(newStart),
        end: formatTime(newEnd),
      });

      updated[day] = {
        ...updated[day],
        times: existingTimes,
      };

      // Validate immediately
      setAvailabilityErrors((prevErrors) => {
        let newErrors = { ...prevErrors };

        // Remove old errors for this day
        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith(day)) {
            delete newErrors[key];
          }
        });

        // Check each slot
        updated[day].times.forEach((t, i) => {
          if (t.start && t.end) {
            const startMinutes = parseTimeToMinutes(t.start);
            const endMinutes = parseTimeToMinutes(t.end);

            if (endMinutes <= startMinutes) {
              newErrors[`${day}_${i}`] =
                "End time must be later than start time.";
              return;
            }

            // Overlap detection - ANY intersection or touching is considered overlapping
            const overlaps = updated[day].times.some((other, j) => {
              if (i === j || !other.start || !other.end) return false;
              const oStart = parseTimeToMinutes(other.start);
              const oEnd = parseTimeToMinutes(other.end);

              // Overlapping includes touching boundaries
              return (
                (startMinutes >= oStart && startMinutes < oEnd) || // Start falls within existing range
                (endMinutes > oStart && endMinutes <= oEnd) || // End falls within existing range
                (startMinutes < oStart && endMinutes > oEnd) || // New range completely contains existing
                startMinutes === oEnd || // New range starts where existing ends
                endMinutes === oStart // New range ends where existing starts
              );
            });

            if (overlaps) {
              newErrors[`${day}_${i}`] =
                "Time range overlaps with an existing slot.";
            }
          }
        });

        return newErrors;
      });

      return updated;
    });
  };

  const handleRemoveTimeRange = (day, idx) => {
    setEditAvailability((prev) => {
      const updated = { ...prev };
      const updatedTimes = [...updated[day].times];
      updatedTimes.splice(idx, 1);
      updated[day] = {
        ...updated[day],
        times: updatedTimes,
      };

      // After removing, re-validate all time slots for this day
      setAvailabilityErrors((prevErrors) => {
        let newErrors = { ...prevErrors };

        // Remove all errors for this day (we'll re-check them)
        Object.keys(newErrors).forEach((key) => {
          if (key.startsWith(day)) {
            delete newErrors[key];
          }
        });

        // Re-check each slot for overlaps or invalid ranges
        updatedTimes.forEach((t, i) => {
          if (t.start && t.end) {
            const startMinutes = parseTimeToMinutes(t.start);
            const endMinutes = parseTimeToMinutes(t.end);

            // Rule 1: end > start
            if (endMinutes <= startMinutes) {
              newErrors[`${day}_${i}`] =
                "End time must be later than start time.";
              return; // skip further checks
            }

            // Rule 2: no overlaps
            const overlaps = updatedTimes.some((other, j) => {
              if (i === j || !other.start || !other.end) return false;
              const oStart = parseTimeToMinutes(other.start);
              const oEnd = parseTimeToMinutes(other.end);

              return (
                (startMinutes >= oStart && startMinutes <= oEnd) ||
                (endMinutes >= oStart && endMinutes <= oEnd) ||
                (startMinutes < oStart && endMinutes > oEnd) ||
                startMinutes === oStart ||
                endMinutes === oEnd
              );
            });

            if (overlaps) {
              newErrors[`${day}_${i}`] =
                "Time range overlaps with an existing slot.";
            }
          }
        });

        return newErrors;
      });

      return updated;
    });
  };

  const handleSaveAvailability = () => {
    setTempBranch((prev) => ({
      ...prev,
      availableHours: editAvailability,
    }));

    // If you want to see the updated tempBranch:
    // useEffect(() => { console.log(tempBranch); }, [tempBranch]);

    setShowEditAvailabilityModal(false); // close modal
  };

  const [fetchingKeetahours, setFetchingKeetahours] = useState(false);
  const fetchKeetaBusinessHours = async () => {
    setFetchingKeetahours(true); // start spinning
    try {
      const response = await api.get(
        `/branch/${branchId}/keeta-business-hours`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      const businessHours = response.data?.data?.businessHours?.raw || {};

      const dayMap = {
        mon: "monday",
        tue: "tuesday",
        wed: "wednesday",
        thu: "thursday",
        fri: "friday",
        sat: "saturday",
        sun: "sunday",
      };

      const formatTime = (seconds) => {
        const hrs = Math.floor(seconds / 3600)
          .toString()
          .padStart(2, "0");
        const mins = Math.floor((seconds % 3600) / 60)
          .toString()
          .padStart(2, "0");
        return `${hrs}:${mins}`;
      };

      const updatedAvailability = { ...editAvailability }; // start with current state

      Object.entries(dayMap).forEach(([shortDay, fullDay]) => {
        const timesFromApi = businessHours[shortDay] || [];

        const mappedTimes = timesFromApi.map((t) => ({
          start: formatTime(parseInt(t.startTime, 10)),
          end: formatTime(parseInt(t.endTime, 10)),
        }));

        // Check if all times are 00:00–00:00
        const hasValidTime = mappedTimes.some(
          (t) => t.start !== "00:00" || t.end !== "00:00"
        );

        updatedAvailability[fullDay] = {
          // keep old times if API returns 00:00–00:00
          times: hasValidTime
            ? mappedTimes
            : updatedAvailability[fullDay]?.times || [],
          is_visible: hasValidTime, // true if valid time, false otherwise
        };
      });

      setEditAvailability(updatedAvailability);

      toast.success(
        response.data?.message || `${t("Keeta hours synced successfully!")}`
      );
    } catch (error) {
      console.error("Error fetching Keeta business hours:", error);
      toast.error(
        error.response?.data?.message || `${t("Failed to sync Keeta hours.")}`
      );
    } finally {
      setFetchingKeetahours(false); // stop spinning
    }
  };

  const handleScheduleClose = () => {
    if (tempBranch?.availableHours) {
      setEditAvailability(tempBranch.availableHours);
    } else {
      setEditAvailability({}); // fallback to empty object
    }

    setShowEditAvailabilityModal(false);
  };

  // Add this fetch function
  const fetchAllKeetaShops = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/keeta-shops/store/${storeId}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKeetaShops(response.data.data?.shops || []);
    } catch (error) {
      console.error("Error fetching keetaShops:", error);
      setKeetaShops([]);
    }
  };

  const fetchAllFoodicsShops = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/foodics-shops/store/${storeId}/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFoodicsShops(response.data.data?.shops || []);
    } catch (error) {
      console.error("Error fetching foodicsShops:", error);
      setFoodicsShops([]);
    }
  };

  // Add this useEffect to fetch keeta and foodics shops when component mounts
  useEffect(() => {
    if (storeId) {
      fetchAllKeetaShops();
      fetchAllFoodicsShops();
      fetchStoreDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  useEffect(() => {
    if (
      keetaShopDetails &&
      keetaShopDetails.keetaShopId &&
      keetaShops.length > 0
    ) {
      // Find the shop that matches either by keetaShopId or _id
      const matchingShop = keetaShops.find(
        (shop) =>
          shop.keetaShopId === keetaShopDetails.keetaShopId ||
          shop._id === keetaExternalId
      );

      if (matchingShop) {
        setSelectedKeetaShopId(matchingShop._id);
      }
    } else {
      // Clear selected shop if no keeta shop details
      setSelectedKeetaShopId("");
    }
  }, [keetaShopDetails, keetaShops, keetaExternalId]);

  useEffect(() => {
    if (
      foodicsShopDetails &&
      foodicsShopDetails.foodicsBranchId &&
      foodicsShops.length > 0
    ) {
      // Find the shop that matches either by foodicsBranchId or _id
      const matchingShop = foodicsShops.find(
        (shop) =>
          shop.foodicsBranchId === foodicsShopDetails.foodicsBranchId ||
          shop._id === foodicsExternalId
      );

      if (matchingShop) {
        setSelectedFoodicsShopId(matchingShop._id);
      }
    } else {
      // Clear selected shop if no foodics shop details
      setSelectedFoodicsShopId("");
    }
  }, [foodicsShopDetails, foodicsShops, foodicsExternalId]);
  const filterKeetaShopsLinkedToOtherBranches = () => {
    const currentBranch = branches.find((branch) => branch._id === branchId);
    const currentBranchKeetaShopIds = new Set();

    if (currentBranch?.syncedApps?.length) {
      currentBranch.syncedApps.forEach((app) => {
        if (app.app === "Keeta" && app.external_id) {
          currentBranchKeetaShopIds.add(app.external_id);
        }
      });
    }

    // Step 2: Create a Set of all keetaShopIds that are linked in any branch
    const allLinkedKeetaShopIds = new Set();

    branches.forEach((branch) => {
      const syncedApps = branch.syncedApps || [];
      syncedApps.forEach((app) => {
        if (app.app === "Keeta" && app.external_id) {
          allLinkedKeetaShopIds.add(app.external_id);
        }
      });
    });

    // Step 3: Filter keetaShops
    const result = keetaShops.filter((shop) => {
      const keetaShopId = shop.keetaShopId;

      // If it's linked somewhere, but NOT in current branch → filter it out
      return (
        !allLinkedKeetaShopIds.has(keetaShopId) || // not linked in any branch
        currentBranchKeetaShopIds.has(keetaShopId) // linked in current branch
      );
    });

    setFilteredKeetaShops(result);
  };

  const filterFoodicsShopsLinkedToOtherBranches = () => {
    const currentBranch = branches.find((branch) => branch._id === branchId);
    const currentBranchFoodicsShopIds = new Set();

    if (currentBranch?.syncedApps?.length) {
      currentBranch.syncedApps.forEach((app) => {
        if (app.app === "Foodics" && app.external_id) {
          currentBranchFoodicsShopIds.add(app.external_id);
        }
      });
    }

    // Step 2: Create a Set of all foodicsShopIds that are linked in any branch
    const allLinkedFoodicsShopIds = new Set();

    branches.forEach((branch) => {
      const syncedApps = branch.syncedApps || [];
      syncedApps.forEach((app) => {
        if (app.app === "Foodics" && app.external_id) {
          allLinkedFoodicsShopIds.add(app.external_id);
        }
      });
    });

    // Step 3: Filter foodicsShops
    const result = foodicsShops.filter((shop) => {
      const foodicsShopId = shop.foodicsBranchId;

      // If it's linked somewhere, but NOT in current branch → filter it out
      return (
        !allLinkedFoodicsShopIds.has(foodicsShopId) || // not linked in any branch
        currentBranchFoodicsShopIds.has(foodicsShopId) // linked in current branch
      );
    });

    setFilteredFoodicsShops(result);
  };

  useEffect(() => {
    if (keetaShops.length && branches.length && branchId) {
      filterKeetaShopsLinkedToOtherBranches();
    }
  }, [keetaShops, branches, branchId]);

  useEffect(() => {
    if (foodicsShops.length && branches.length && branchId) {
      filterFoodicsShopsLinkedToOtherBranches();
    }
  }, [foodicsShops, branches, branchId]);

  // Initialize sync toggles based on branch syncedApps
  useEffect(() => {
    if (branch?.syncedApps && apps.length > 0) {
      const toggleStates = {};
      apps.forEach((app) => {
        const appName = app.application?.toLowerCase();
        if (appName?.includes("ninja") || appName?.includes("jahez")) {
          // Check if this app exists in syncedApps (regardless of synced_status)
          const syncedApp = branch.syncedApps.find((s) =>
            s.app?.toLowerCase()?.includes(appName)
          );
          toggleStates[app._id] = !!syncedApp;
        }
      });
      setSyncToggles(toggleStates);
    }
  }, [branch, apps]);
  // Add this function to filter keeta shops based on selected app

  const MANDATORY_FIELDS = [
    "name",
    "email",
    "phone",
    "city",
    "address",
    "latitude",
    "longitude",
  ];

  const initialFieldErrors = {
    name: "",
    email: "",
    phone: "",
    city: "",
    address: "",
    latitude: "",
    longitude: "",
    name_ar: "",
  };

  const validateField = (fieldName, value) => {
    let errorMessage = "";
    switch (fieldName) {
      case "name":
        if (!value || !value.trim()) {
          errorMessage = `${t("Branch name is required")}`;
        } else if (value.trim().length > 23) {
          errorMessage = `${t("Branch name must not exceed 23 characters")}`;
        }
        break;
      case "name_ar":
        if (value && value.trim()) {
          const arabicRegex = /^[\u0600-\u06FF0-9 ]+$/;
          if (!arabicRegex.test(value.trim())) {
            errorMessage = `${t("Only Arabic letters, numbers, and spaces are allowed")}`;
          }
        }
        break;
      case "email":
        if (!value || !value.trim()) {
          errorMessage = `${t("Email is required")}`;
        } else if (!/\S+@\S+\.\S+/.test(value)) {
          errorMessage = `${t("Please enter a valid email address")}`;
        }
        break;
      case "phone":
        if (!value || !value.trim()) {
          errorMessage = `${t("Phone number is required")}`;
        } else {
          const cleanPhone = value.replace(/\D/g, "");
          if (
            (cleanPhone.length === 13 && cleanPhone.startsWith("966")) ||
            (cleanPhone.length === 12 && cleanPhone.startsWith("966")) ||
            (cleanPhone.length === 10 && cleanPhone.startsWith("05")) ||
            (cleanPhone.length === 9 && cleanPhone.startsWith("5"))
          ) {
            // Valid KSA format
          } else {
            errorMessage = `${t(
              "Please enter a valid KSA phone number (e.g., +966501234567, 966501234567, 0501234567, or 501234567)"
            )}`;
          }
        }
        break;
      case "city":
        if (!value || !value.trim()) {
          errorMessage = `${t("City is required")}`;
        }
        break;
      case "address":
        if (!value || !value.trim()) {
          errorMessage = `${t("Address is required")}`;
        }
        break;
      case "latitude":
        if (!value || !value.trim()) {
          errorMessage = `${t("Latitude is required")}`;
        } else if (isNaN(value) || value < -90 || value > 90) {
          errorMessage = `${t("Please enter a valid latitude (-90 to 90)")}`;
        }
        break;
      case "longitude":
        if (!value || !value.trim()) {
          errorMessage = `${t("Longitude is required")}`;
        } else if (isNaN(value) || value < -180 || value > 180) {
          errorMessage = `${t("Please enter a valid longitude (-180 to 180)")}`;
        }
        break;
      default:
        break;
    }
    return errorMessage;
  };

  const [fieldErrors, setFieldErrors] = useState(initialFieldErrors);

  const handleFieldChangeValidated = (field, value) => {
    setTempBranch((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => ({
      ...prev,
      [field]: validateField(field, value),
    }));
    setError("");
  };

  const validateAllFields = () => {
    let hasErrors = false;
    const newErrors = { ...initialFieldErrors };
    MANDATORY_FIELDS.forEach((field) => {
      const value = tempBranch[field];
      const errorMsg = validateField(field, value);
      newErrors[field] = errorMsg;
      if (errorMsg) hasErrors = true;
    });
    setFieldErrors(newErrors);
    return !hasErrors;
  };

  const fetchBranchDetails = async () => {
    try {
      if (!storeId || !branchId) {
        throw new Error("Missing store or branch ID");
      }

      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/${storeId}/branch/${branchId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = response.data;
            setBranch(data);
      setTempBranch(JSON.parse(JSON.stringify(data)));
    } catch (err) {
      setError(err.message || `${t("Failed to load branch details")}`);
      console.error("Error fetching branch:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchApps = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await api.get("/apps", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setApps(response.data.apps || []);
      } catch (err) {
        console.error("Error fetching apps:", err.message);
      }
    };
    const fetchCities = async () => {
      try {
        const token = localStorage.getItem("token");
        setLoadingCities(true);
        const response = await api.get("/cities", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCities(response.data.data);
        setLoadingCities(false);
      } catch (err) {
        console.error("Error fetching cities:", err);
        setError(`${t("Failed to load cities. Please try again later.")}`);
        setLoadingCities(false);
      }
    };

    fetchCities();

    fetchBranchDetails();
    fetchApps();
  }, [storeId, branchId, branchRegister]);

  const handleEditToggle = () => {
    setEditMode(!editMode);
    if (editMode) {
      // Reset changes when canceling edit
      setTempBranch(JSON.parse(JSON.stringify(branch)));
      setAddingSlot(null); // Cancel any in-progress additions
      // Reset selected keeta shop to current state
      if (keetaShopDetails && keetaShops.length > 0) {
        const matchingShop = keetaShops.find(
          (shop) =>
            shop.keetaShopId === keetaShopDetails.keetaShopId ||
            shop._id === keetaExternalId
        );
        setSelectedKeetaShopId(matchingShop ? matchingShop._id : "");
      } else {
        setSelectedKeetaShopId("");
      }
      // Reset selected foodics shop to current state
      if (foodicsShopDetails && foodicsShops.length > 0) {
        const matchingShop = foodicsShops.find(
          (shop) =>
            shop.foodicsBranchId === foodicsShopDetails.foodicsBranchId ||
            shop._id === foodicsExternalId
        );
        setSelectedFoodicsShopId(matchingShop ? matchingShop._id : "");
      } else {
        setSelectedFoodicsShopId("");
      }
      // Clear validation and status messages
      setFieldErrors(initialFieldErrors);
      setError("");
      setSuccessMessage("");
      setSlotErrors({});
      setAvailabilityErrors({});
    }
  };

  const handleFieldChange = (field, value, index = null, subField = null) => {
    const updated = { ...tempBranch };

    if (index !== null) {
      // For array fields (availability, breakHours, holidayHours)
      if (subField) {
        updated[field][index][subField] = value;
      } else {
        updated[field][index] = value;
      }
    } else {
      // For direct fields
      updated[field] = value;
    }

    setTempBranch(updated);
  };

  const handleSaveChanges = async () => {
    if (!validateAllFields()) {
      setSuccessMessage("");
      return;
    }
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      console.log("🔄 Sending Data to Update Branch:", tempBranch);

      const payload = {
        ...tempBranch,
        name: {
          en: tempBranch?.name || "",
          ...(tempBranch?.name_ar ? { ar: tempBranch.name_ar } : {}),
        },
        // Send keetaShopId (could be empty string to clear the association)
        keetaShopId: selectedKeetaShopId || "",
        // Send foodicsBranchId as internal shop _id (ObjectId)
        foodicsBranchId: selectedFoodicsShopId || "",
      };

      const saveResponse = await api.put(`/stores/${storeId}/branch/${branchId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const saveWarnings = saveResponse.data?.warnings;
      if (Array.isArray(saveWarnings) && saveWarnings.length > 0) {
        saveWarnings.forEach((w) => {
          const m = w?.message || "";
          const tr = t(m);
          toast.warning(tr !== m ? tr : m);
        });
      }

      setBranch(tempBranch);
      setEditMode(false);
      setAddingSlot(null);
      toast.success(`${t("Branch updated successfully.")}`);

      // Refresh data after successful update
      await fetchBranchDetails();

      // If keetaShopId was cleared, also clear the local state
      if (!selectedKeetaShopId) {
        setKeetaShopDetails(null);
      } else {
        // Refresh keeta shop details if a shop was selected
        await fetchKeetaAppDetails();
      }

      // If foodicsShopId was cleared, also clear the local state
      if (!selectedFoodicsShopId) {
        setFoodicsShopDetails(null);
      } else {
        // Refresh foodics shop details if a shop was selected
        await fetchFoodicsAppDetails();
      }

      setError("");
      setFieldErrors(initialFieldErrors);
    } catch (err) {
      console.log(err);
      const errors = err?.response?.data?.errors;
      const firstError = errors ? Object.values(errors)[0] : null;

      toast.error(
        firstError ||
          resolveTenantApiError(err, t, "Failed to update branch")
      );
      // If server returns field-level errors, show them inline and stay in edit mode
      // if (err?.response?.data?.errors) {
      //   const apiErrors = err.response.data.errors;
      //   setFieldErrors((prev) => ({ ...prev, ...apiErrors }));
      //   setError("");
      //   setSuccessMessage("");
      //   // Stay in edit mode
      //   return;
      // }

      // For fatal errors, show the first available error or fallback

      setSuccessMessage("");
      console.error("Error updating branch:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSlot = (slotType) => {
    setAddingSlot(slotType);
    // Add a new empty slot to the beginning of the array
    const updated = { ...tempBranch };
    updated[slotType] = [{ day: "", start: "", end: "" }, ...updated[slotType]];
    setTempBranch(updated);
  };

  const handleSaveNewSlot = (slotType) => {
    // Validation could be added here
    setAddingSlot(null);
  };

  const handleCancelAddSlot = (slotType) => {
    const updated = { ...tempBranch };
    // Remove the first item if it's empty (the one we were adding)
    if (
      updated[slotType][0] &&
      !updated[slotType][0].day &&
      !updated[slotType][0].start &&
      !updated[slotType][0].end
    ) {
      updated[slotType] = updated[slotType].slice(1);
    }
    setTempBranch(updated);
    setAddingSlot(null);
  };

  const handleDeleteSlot = (slotType, index) => {
    const updated = { ...tempBranch };
    updated[slotType] = updated[slotType].filter((_, i) => i !== index);
    setTempBranch(updated);
  };

  const formatTime = (time) => {
    if (!time) return "N/A";
    return time.includes(":") ? time : `${time.slice(0, 2)}:${time.slice(2)}`;
  };

  const handleIntegrationClick = async (app) => {
    const payload = {
      appId: app._id,
      branchId,
    };

    try {
      const token = localStorage.getItem("token");

      console.log(
        "🔄 Sending Data to Register Branch to third party app:",
        payload
      );

      const response = await api.post("/register-branch", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("✅ Branch Registered Successfully:", response.data);
      toast.success(`${t("Branch Register successfully.")}`);
      // Optionally handle success UI or state update here
      setNewBranchRegistered(response.data);
    } catch (error) {
      toast.error(
        error.response?.data?.message || `${t("Error registering branch.")}`
      );
      console.error(
        "❌ Error Registering Branch:",
        error.response?.data || error.message
      );
    }
  };

  const handleRemoveIntegration = async (app) => {};

  const handleSyncToggle = async (app, newStatus) => {
    const appId = app._id;
    setTogglingSync((prev) => ({ ...prev, [appId]: true }));

    try {
      const token = localStorage.getItem("token");
      const payload = {
        appId: appId,
        branchId: branchId,
        status: newStatus,
      };

      console.log("🔄 Syncing Branch with App:", payload);

      const response = await api.post("/sync-branch", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log("✅ Branch Sync Response:", response.data);

      // Update the toggle state
      setSyncToggles((prev) => ({ ...prev, [appId]: newStatus }));

      // Show success toast
      toast.success(`${app.application} ${t("app synced successfully.")}`);

      // Refresh branch details to get updated syncedApps
      await fetchBranchDetails();
    } catch (error) {
      console.error(
        "❌ Error Syncing Branch:",
        error.response?.data || error.message
      );
      toast.error(
        error.response?.data?.message ||
          `${t("Error syncing")} ${app.application}`
      );

      // Revert toggle state on error
      setSyncToggles((prev) => ({ ...prev, [appId]: !newStatus }));
    } finally {
      setTogglingSync((prev) => ({ ...prev, [appId]: false }));
    }
  };

  const renderEditableField = (value, onChange, placeholder = "") => (
    <NativeInput
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="form-control-sm"
    />
  );

  const handleSlotFieldChange = (field, index, subField, value) => {
    setTempBranch((prev) => {
      const updated = { ...prev };
      updated[field][index][subField] = value;
      return updated;
    });
    // Validate slot
    validateSlotField(field, index, subField, value);
  };

  const validateSlotField = (field, index, subField, value) => {
    setSlotErrors((prev) => {
      const key = `${field}_${index}`;
      const slot = tempBranch[field][index];
      let errors = prev[key] || { day: "", start: "", end: "" };
      if (subField === "day") {
        errors.day = !value ? `${t("Day is required")}` : "";
      }
      if (subField === "start" || subField === "end") {
        const start = subField === "start" ? value : slot.start;
        const end = subField === "end" ? value : slot.end;
        if (!isValidTime(start)) {
          errors.start = `${t("Start time must be in HH:MM format")}`;
        } else {
          errors.start = "";
        }
        if (!isValidTime(end)) {
          errors.end = `${t("End time must be in HH:MM format")}`;
        } else {
          errors.end = "";
        }
        if (isValidTime(start) && isValidTime(end) && start >= end) {
          errors.start = `${t("Start time must be before end time")}`;
          errors.end = `${t("End time must be after start time")}`;
        }
      }
      return { ...prev, [key]: errors };
    });
  };

  const getLatestSyncForApp = (appName) => {
    if (!branch?.syncedApps) return null;
    // Filter for this app
    const syncs = branch.syncedApps.filter(
      (s) => s.app && s.app.toLowerCase() === appName.toLowerCase()
    );
    if (!syncs.length) return null;
    // Return the latest by synced_date
    return syncs.reduce((latest, curr) => {
      if (!latest) return curr;
      return new Date(curr.synced_date) > new Date(latest.synced_date)
        ? curr
        : latest;
    }, null);
  };

  const renderTimeSlotTables = () => {
    const fields = [
      // { name: "breakHours", title: "Break Hours" },
      // { name: "holidayHours", title: "Holiday Hours" },
    ];

    return (
      <div className="row p-0 g-4">
        {fields.map((field) => (
          <div className="col-12 col-md-6" key={field.name}>
            <MDBCard className="h-100">
              <MDBCardBody>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <MDBCardTitle>{field.title}</MDBCardTitle>
                  {editMode && (
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => handleAddSlot(field.name)}
                      disabled={addingSlot === field.name}
                    >
                      <MDBIcon icon="plus" className="me-1" />
                      Add
                    </Button>
                  )}
                </div>

                <MDBTable responsive>
                  <MDBTableHead>
                    <tr>
                      <th>{t("Day")}</th>
                      <th>{t("Start Time")}</th>
                      <th>{t("End Time")}</th>
                      {editMode && <th>{t("actions")}</th>}
                    </tr>
                  </MDBTableHead>
                  <MDBTableBody>
                    {tempBranch[field.name]?.map((slot, index) => {
                      const slotKey = `${field.name}_${index}`;
                      const errors = slotErrors[slotKey] || {};
                      return (
                        <tr key={slot._id || index}>
                          <td>
                            {editMode ? (
                              <>
                                <select
                                  className="form-select form-select-sm"
                                  value={slot.day || ""}
                                  onChange={(e) =>
                                    handleSlotFieldChange(
                                      field.name,
                                      index,
                                      "day",
                                      e.target.value
                                    )
                                  }
                                >
                                  <option value="">Select Day</option>
                                  {DAYS_OF_WEEK.map((day) => (
                                    <option key={day} value={day}>
                                      {day.charAt(0).toUpperCase() +
                                        day.slice(1)}
                                    </option>
                                  ))}
                                </select>
                                {errors.day && (
                                  <div className="text-danger small mt-1">
                                    {errors.day}
                                  </div>
                                )}
                              </>
                            ) : (
                              slot.day || "N/A"
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <>
                                <input
                                  type="time"
                                  className="form-control form-control-sm"
                                  value={slot.start || ""}
                                  onChange={(e) =>
                                    handleSlotFieldChange(
                                      field.name,
                                      index,
                                      "start",
                                      e.target.value
                                    )
                                  }
                                />
                                {errors.start && (
                                  <div className="text-danger small mt-1">
                                    {errors.start}
                                  </div>
                                )}
                              </>
                            ) : (
                              formatTime(slot.start)
                            )}
                          </td>
                          <td>
                            {editMode ? (
                              <>
                                <input
                                  type="time"
                                  className="form-control form-control-sm"
                                  value={slot.end || ""}
                                  onChange={(e) =>
                                    handleSlotFieldChange(
                                      field.name,
                                      index,
                                      "end",
                                      e.target.value
                                    )
                                  }
                                />
                                {errors.end && (
                                  <div className="text-danger small mt-1">
                                    {errors.end}
                                  </div>
                                )}
                              </>
                            ) : (
                              formatTime(slot.end)
                            )}
                          </td>
                          {editMode && (
                            <td>
                              {addingSlot === field.name && index === 0 ? (
                                <>
                                  <Button
                                    color="success"
                                    size="sm"
                                    className="me-2"
                                    onClick={() =>
                                      handleSaveNewSlot(field.name)
                                    }
                                  >
                                    <MDBIcon icon="check" />
                                  </Button>
                                  <Button
                                    color="danger"
                                    size="sm"
                                    onClick={() =>
                                      handleCancelAddSlot(field.name)
                                    }
                                  >
                                    <MDBIcon icon="times" />
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  color="danger"
                                  size="sm"
                                  onClick={() =>
                                    handleDeleteSlot(field.name, index)
                                  }
                                  disabled={addingSlot === field.name}
                                >
                                  <MDBIcon icon="trash" />
                                </Button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </MDBTableBody>
                </MDBTable>
              </MDBCardBody>
            </MDBCard>
          </div>
        ))}
      </div>
    );
  };

  if (loading && !editMode) {
    return (
      <MDBContainer className="text-center my-5">
        <MDBSpinner role="status">
          <span className="visually-hidden">{t("loading")}...</span>
        </MDBSpinner>
        <p>{t("Loading branch details")}...</p>
      </MDBContainer>
    );
  }

  if (
    error &&
    (!branch ||
      (!error.includes("not found") &&
        !error.includes("Missing store or branch ID")))
  ) {
    return (
      <MDBContainer className="text-center my-5">
        <MDBIcon
          icon="exclamation-triangle"
          size="2x"
          className="text-danger"
        />
        <p className="text-danger">{error}</p>
      </MDBContainer>
    );
  }

  if (!branch) {
    return (
      <MDBContainer className="text-center my-5">
        <MDBIcon icon="question-circle" size="2x" />
        <p>{t("Branch not found")}</p>
      </MDBContainer>
    );
  }

  return (
    <MDBContainer fluid className="py-4">
      <Actionbar
        title={t("quickAction")}
        subtitle={t("manageYouWorkSpace")}
        firstBtnText={editMode ? `${t("cancel")}` : `${t("back")}`}
        firstBtnOnClick={
          editMode
            ? () => handleEditToggle() // Cancel editing
            : () => navigate(-1) // Go back
        }
        firstBtnClass={editMode ? "reject-orders" : "secondary"}
        firstBtnIcon={editMode ? "cross" : "back"}
        secondBtnText={editMode ? `${t("savechanges")}` : `${t("editbranch")}`}
        secondBtnOnClick={
          editMode
            ? () => {
                handleSaveChanges(); // Save changes
              }
            : () => setEditMode(true) // Enable edit mode
        }
        secondBtnClass={editMode ? "accept-orders" : "ocean"}
        secondBtnIcon={editMode ? "save" : "pen"}
      />

      <MDBRow className="mb-4">
        <MDBCol>
          <MDBCard className="shadow-sm">
            <MDBCardBody className="p-4">
              {/* Header Section */}
              <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                <MDBCardTitle className="fw-bold mb-0 w-100">
                  {editMode ? (
                    <div className="row g-2 w-100">
                      <div className="col-md-6">
                        <NativeInput
                          value={tempBranch?.name || ""}
                          onChange={(e) =>
                            handleFieldChangeValidated("name", e.target.value)
                          }
                          placeholder={t("branchnameenglish")}
                          className="form-control-lg w-100"
                          style={{
                            fontSize: "1.5rem",
                            fontWeight: 600,
                            minWidth: 300,
                            maxWidth: 600,
                          }}
                          autoFocus
                          invalid={!!fieldErrors.name}
                          feedback={fieldErrors.name}
                        />
                      </div>
                      <div className="col-md-6">
                        <NativeInput
                          value={tempBranch?.name_ar || ""}
                          onChange={(e) =>
                            handleFieldChangeValidated("name_ar", e.target.value)
                          }
                          placeholder={t("branchnamearabic")}
                          className="form-control-lg w-100"
                          style={{
                            fontSize: "1.5rem",
                            fontWeight: 600,
                            minWidth: 300,
                            maxWidth: 600,
                          }}
                          invalid={!!fieldErrors.name_ar}
                          feedback={fieldErrors.name_ar}
                        />
                      </div>
                    </div>
                  ) : (
                    <span
                      className="text-primary"
                      style={{ fontSize: "1.5rem", fontWeight: 600 }}
                    >
                      {isArabic
                        ? branch?.name?.ar || branch?.name_ar || branch?.name?.en || branch?.name
                        : branch?.name?.en || branch?.name}
                    </span>
                  )}
                </MDBCardTitle>
              </div>

              {/* Branch Details Grid */}
              <div className="row g-4">
                {/* Column 1 */}
                <div className="col-md-6">
                  <div className="detail-item mb-4">
                    <label className="text-muted small mb-1">
                      {t("email")}
                    </label>
                    {editMode ? (
                      <NativeInput
                        value={tempBranch.email || ""}
                        onChange={(e) =>
                          handleFieldChangeValidated("email", e.target.value)
                        }
                        placeholder={t("email")}
                        className="form-control"
                      />
                    ) : (
                      <div className="fw-normal">{branch.email || "N/A"}</div>
                    )}
                    {fieldErrors.email && (
                      <div className="text-danger small mt-1">
                        {fieldErrors.email}
                      </div>
                    )}
                  </div>

                  <div className="detail-item mb-4">
                    <label className="text-muted small mb-1">
                      {t("address")}
                    </label>
                    {editMode ? (
                      <NativeInput
                        value={tempBranch.address || ""}
                        onChange={(e) =>
                          handleFieldChangeValidated("address", e.target.value)
                        }
                        placeholder={t("address")}
                        className="form-control"
                      />
                    ) : (
                      <div className="fw-normal">{branch.address || "N/A"}</div>
                    )}
                    {fieldErrors.address && (
                      <div className="text-danger small mt-1">
                        {fieldErrors.address}
                      </div>
                    )}
                  </div>

                  <div className="detail-item mb-4">
                    <label className="text-muted small mb-1">
                      {t("phone")}
                    </label>
                    {editMode ? (
                      <NativeInput
                        value={tempBranch.phone || ""}
                        onChange={(e) =>
                          handleFieldChangeValidated("phone", e.target.value)
                        }
                        placeholder={t("phone")}
                        className="form-control"
                      />
                    ) : (
                      <div className="fw-normal">{branch.phone || "N/A"}</div>
                    )}
                    {fieldErrors.phone && (
                      <div className="text-danger small mt-1">
                        {fieldErrors.phone}
                      </div>
                    )}
                  </div>
                </div>

                {/* Column 2 */}
                <div className="col-md-6">
                  <div className="detail-item mb-4">
                    <label className="text-muted small mb-1">
                      {t("integrationid")}
                      <MDBTooltip
                        tag="span"
                        title="Unique identifier for this branch."
                      >
                        <MDBIcon
                          icon="info-circle"
                          className="ms-1 text-info"
                        />
                      </MDBTooltip>
                    </label>
                    {editMode ? (
                      <NativeInput
                        value={tempBranch._id || ""}
                        placeholder="BRN-12345"
                        className="form-control"
                        disabled
                      />
                    ) : (
                      <div className="d-flex align-items-center gap-2">
                        <code className="bg-light p-2 rounded flex-grow-1 text-truncate">
                          {branch._id || "N/A"}
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(branch._id);
                            setCopiedId(branch._id);
                            setTimeout(() => setCopiedId(null), 2000);
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            padding: "4px",
                            borderRadius: "4px",
                            display: "flex",
                            alignItems: "center",
                            color:
                              copiedId === branch._id ? "#28a745" : "#6c757d",
                          }}
                          title="Copy Integration ID"
                          disabled={!branch._id}
                        >
                          {copiedId === branch._id ? (
                            <Check size={16} />
                          ) : (
                            <Copy size={16} />
                          )}
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="row g-2 mb-4">
                    <div className="col-6">
                      <label className="text-muted small mb-1">
                        {t("latitude")}
                      </label>
                      {editMode ? (
                        <NativeInput
                          value={tempBranch.latitude || ""}
                          onChange={(e) =>
                            handleFieldChangeValidated(
                              "latitude",
                              e.target.value
                            )
                          }
                          placeholder="24.7136"
                          className="form-control"
                        />
                      ) : (
                        <div className="fw-normal">
                          {branch.latitude || "N/A"}
                        </div>
                      )}
                      {fieldErrors.latitude && (
                        <div className="text-danger small mt-1">
                          {fieldErrors.latitude}
                        </div>
                      )}
                    </div>
                    <div className="col-6">
                      <label className="text-muted small mb-1">
                        {t("longitude")}
                      </label>
                      {editMode ? (
                        <NativeInput
                          value={tempBranch.longitude || ""}
                          onChange={(e) =>
                            handleFieldChangeValidated(
                              "longitude",
                              e.target.value
                            )
                          }
                          placeholder="46.6753"
                          className="form-control"
                        />
                      ) : (
                        <div className="fw-normal">
                          {branch.longitude || "N/A"}
                        </div>
                      )}
                      {fieldErrors.longitude && (
                        <div className="text-danger small mt-1">
                          {fieldErrors.longitude}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="detail-item mb-4">
                    <label className="text-muted small mb-1">{t("city")}</label>
                    {editMode ? (
                      <div className="form-group">
                        <select
                          className="form-select"
                          value={tempBranch.city || ""}
                          onChange={(e) =>
                            handleFieldChangeValidated("city", e.target.value)
                          }
                          disabled={loadingCities}
                        >
                          <option value="">Select a city</option>
                          {cities.map((cityItem) => (
                            <option key={cityItem._id} value={cityItem.name}>
                              {cityItem.name} ({cityItem.name_localized})
                            </option>
                          ))}
                        </select>
                        {loadingCities && (
                          <div className="mt-2 text-muted small">
                            <MDBSpinner size="sm" className="me-2" />
                            {t("Loading cities")}...
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="fw-normal">{branch.city || "N/A"}</div>
                    )}
                    {fieldErrors.city && (
                      <div className="text-danger small mt-1">
                        {fieldErrors.city}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Shop Integration Section */}
              {(keetaAvailable || foodicsAvailable) && (
                <div className="row g-4 pb-4 mb-4">
                  <div className="col-12">
                    <div className="row g-3">
                      {/* Keeta Shops Dropdown */}
                      {keetaAvailable && (
                        <div
                          className={
                            keetaAvailable && foodicsAvailable
                              ? "col-md-6"
                              : "col-md-12"
                          }
                        >
                          <div className="form-group">
                            <label className="text-muted small mb-1">
                              {t("keetashop")}
                            </label>
                            {editMode ? (
                              <div
                                style={{
                                  position: "relative",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <select
                                  className="form-control form-select form-control-sm"
                                  value={selectedKeetaShopId}
                                  onChange={(e) =>
                                    setSelectedKeetaShopId(e.target.value)
                                  }
                                  style={{
                                    height: "calc(2.4rem + 2px)",
                                    padding: "0.375rem 0.75rem",
                                    fontSize: "0.875rem",
                                    paddingRight: selectedKeetaShopId
                                      ? "2rem"
                                      : "0.75rem", // space for clear button
                                    flex: 1,
                                    appearance: selectedKeetaShopId
                                      ? "none"
                                      : undefined, // Hide arrow when clear is shown
                                    WebkitAppearance: selectedKeetaShopId
                                      ? "none"
                                      : undefined, // Safari
                                    MozAppearance: selectedKeetaShopId
                                      ? "none"
                                      : undefined, // Firefox
                                    backgroundImage: selectedKeetaShopId
                                      ? "none"
                                      : undefined, // remove dropdown arrow when × is shown
                                  }}
                                >
                                  <option value="">{t("selectshop")}</option>
                                  {filteredKeetaShops.length > 0 ? (
                                    filteredKeetaShops.map((shop) => (
                                      <option key={shop._id} value={shop._id}>
                                        {shop.keetaShopId} - {shop.shopName}
                                      </option>
                                    ))
                                  ) : (
                                    <option value="">
                                      {t("noshopavailable")}
                                    </option>
                                  )}
                                </select>

                                {selectedKeetaShopId && (
                                  <button
                                    type="button"
                                    onClick={() => setSelectedKeetaShopId("")}
                                    style={{
                                      position: "absolute",
                                      right: "4px",
                                      background: "transparent",
                                      border: "none",
                                      fontSize: "1rem",
                                      cursor: "pointer",
                                      color: "#999",
                                      zIndex: 1,
                                    }}
                                    title="Clear selection"
                                  >
                                    <X size={20} />
                                  </button>
                                )}
                              </div>
                            ) : (
                              <div className="fw-normal">
                                {keetaShopDetails
                                  ? `${keetaShopDetails.keetaShopId} - ${keetaShopDetails.shopName}`
                                  : `${t("Not linked to a Keeta shop")}`}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Foodics Shops Dropdown */}
                      {foodicsAvailable && (
                        <div
                          className={
                            keetaAvailable && foodicsAvailable
                              ? "col-md-6"
                              : "col-md-12"
                          }
                        >
                          <div className="form-group">
                            <label className="text-muted small mb-1">
                              {t("foodicsshop")}
                            </label>
                            {editMode ? (
                              <div
                                style={{
                                  position: "relative",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                <select
                                  className="form-control form-select form-control-sm"
                                  value={selectedFoodicsShopId}
                                  onChange={(e) =>
                                    setSelectedFoodicsShopId(e.target.value)
                                  }
                                  style={{
                                    height: "calc(2.4rem + 2px)",
                                    padding: "0.375rem 0.75rem",
                                    fontSize: "0.875rem",
                                    paddingRight: selectedFoodicsShopId
                                      ? "2rem"
                                      : "0.75rem", // space for clear button
                                    flex: 1,
                                    appearance: selectedFoodicsShopId
                                      ? "none"
                                      : undefined, // Hide arrow when clear is shown
                                    WebkitAppearance: selectedFoodicsShopId
                                      ? "none"
                                      : undefined, // Safari
                                    MozAppearance: selectedFoodicsShopId
                                      ? "none"
                                      : undefined, // Firefox
                                    backgroundImage: selectedFoodicsShopId
                                      ? "none"
                                      : undefined, // remove dropdown arrow when × is shown
                                  }}
                                >
                                  <option value="">{t("selectshop")}</option>
                                  {filteredFoodicsShops.length > 0 ? (
                                    filteredFoodicsShops.map((shop) => (
                                      <option key={shop._id} value={shop._id}>
                                        {`${shop.foodicsBranchId} - ${
                                          shop.fullName || shop.branchName
                                        }`}
                                      </option>
                                    ))
                                  ) : (
                                    <option value="">
                                      {t("noshopavailable")}
                                    </option>
                                  )}
                                </select>

                                {selectedFoodicsShopId && (
                                  <button
                                    type="button"
                                    onClick={() => setSelectedFoodicsShopId("")}
                                    style={{
                                      position: "absolute",
                                      right: "4px",
                                      background: "transparent",
                                      border: "none",
                                      fontSize: "1rem",
                                      cursor: "pointer",
                                      color: "#999",
                                      zIndex: 1,
                                    }}
                                    title="Clear selection"
                                  >
                                    <X size={20} />
                                  </button>
                                )}
                              </div>
                            ) : (
                              <div className="fw-normal">
                                {(() => {
                                  let shopDetails = null;
                                  if (foodicsShopDetails) {
                                    shopDetails = foodicsShopDetails;
                                  } else if (selectedFoodicsShopId) {
                                    // Prefer filtered list, fallback to full list
                                    shopDetails =
                                      (filteredFoodicsShops.find(
                                        (s) => s._id === selectedFoodicsShopId
                                      )) ||
                                      (foodicsShops.find(
                                        (s) => s._id === selectedFoodicsShopId
                                      )) ||
                                      null;
                                  } else if (foodicsExternalId) {
                                    // Match by external branch id
                                    shopDetails =
                                      (filteredFoodicsShops.find(
                                        (s) =>
                                          s.foodicsBranchId ===
                                          foodicsExternalId
                                      )) ||
                                      (foodicsShops.find(
                                        (s) =>
                                          s.foodicsBranchId ===
                                          foodicsExternalId
                                      )) ||
                                      null;
                                  }

                                  return shopDetails
                                    ? `${
                                        shopDetails.foodicsBranchId ||
                                        shopDetails.branchId ||
                                        "N/A"
                                      } - ${
                                        shopDetails.fullName ||
                                        shopDetails.branchName ||
                                        shopDetails.shopName ||
                                        "Unknown Shop"
                                      }`
                                    : `${t("Not linked to a Foodics shop")}`;
                                })()}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons and Error/Success Messages */}
              <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
                {error &&
                  error !== "Please fix the errors above before saving." && (
                    <div className="alert alert-danger flex-grow-1 me-3 mb-0">
                      <MDBIcon icon="exclamation-circle" className="me-2" />
                      {error}
                    </div>
                  )}
                {successMessage && (
                  <div className="alert alert-success flex-grow-1 me-3 mb-0">
                    <MDBIcon icon="check-circle" className="me-2" />
                    {successMessage}
                  </div>
                )}
                <div className="d-flex gap-2">
                  <Button
                    color="light"
                    onClick={() => navigate(-1)}
                    className="px-4"
                  >
                    <MDBIcon icon="arrow-left" className="me-2" />
                    {t("back")}
                  </Button>
                  <Button
                    color={editMode ? "danger" : "primary"}
                    onClick={
                      editMode ? handleEditToggle : () => setEditMode(true)
                    }
                    className="px-4"
                  >
                    <MDBIcon
                      icon={editMode ? "times" : "pen"}
                      className="me-2"
                    />
                    {editMode ? `${t("cancel")}` : `${t("editbranch")}`}
                  </Button>
                  {editMode && (
                    <Button
                      color="success"
                      className="px-4"
                      onClick={handleSaveChanges}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <MDBSpinner size="sm" className="me-2" />
                          {t("saving")}...
                        </>
                      ) : (
                        <>
                          <MDBIcon icon="save" className="me-2" />
                          {t("savechanges")}
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
      <MDBRow className="mb-4">
        <MDBCol>
          {apps &&
          apps.some(
            (app) =>
              app.store?._id === storeId &&
              (app.application?.toLowerCase().includes("chefz") ||
                app.application?.toLowerCase()?.includes("ninja") ||
                app.application?.toLowerCase()?.includes("jahez"))
          ) ? (
            <MDBCard className="shadow-sm">
              <MDBCardBody>
                <MDBCardTitle className="fw-bold mb-4">
                  {t("syncbranch")}
                </MDBCardTitle>

                <div className="mt-3 d-flex flex-wrap gap-3">
                  {/* Chefz Apps */}
                  {apps
                    .filter(
                      (app) =>
                        app.store?._id === storeId &&
                        app.application?.toLowerCase().includes("chefz")
                    )
                    .map((app) => {
                      const latestSync = getLatestSyncForApp(app.application);
                      let status = "";
                      let statusColor = "";
                      let showResync = false;
                      let showUnavailable = false;
                      let syncMessage = "";

                      if (latestSync) {
                        if (latestSync.external_id) {
                          status = `${t("Synced")}`;
                          statusColor = "success";
                          showResync = true;
                        } else if (latestSync.synced_status === true) {
                          status = "PENDING";
                          statusColor = "warning";
                        } else {
                          status = `${t("Unavailable")}`;
                          statusColor = "danger";
                          showUnavailable = true;
                          showResync = true;
                          syncMessage = latestSync.sync_message || "";
                        }
                      } else {
                        status = `${t("Not Integrated")}`;
                        statusColor = "secondary";
                      }

                      return (
                        <div key={app._id} className="d-flex flex-column">
                          <div
                            className="d-flex align-items-center justify-content-between gap-3 p-3 border rounded mb-2"
                            style={{ minWidth: "280px" }}
                          >
                            <div className="flex-grow-1">
                              <h6 className="mb-1 fw-semibold">
                                {app.application}
                              </h6>
                              <small
                                className={
                                  status === `${t("Synced")}`
                                    ? "text-success fw-bold"
                                    : "text-muted"
                                }
                              >
                                {status}
                              </small>
                            </div>

                            {/* Sync / Resync buttons only for Chefz */}
                            <div className="d-flex gap-2 align-items-center justify-content-center">
                              {showResync && (
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleIntegrationClick(app)}
                                  className="d-flex align-items-center"
                                >
                                  <MDBIcon icon="sync" className="me-2" />
                                  {t("resyncagain")}
                                </Button>
                              )}

                              {status === "Not Integrated" && (
                                <Button
                                  color="success"
                                  size="sm"
                                  onClick={() => handleIntegrationClick(app)}
                                  className="d-flex align-items-center"
                                >
                                  <MDBIcon
                                    icon="plus-circle"
                                    className="me-2"
                                  />
                                  {t("sync")}
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Error messages for Chefz */}
                          {showUnavailable && syncMessage && (
                            <div
                              className="alert alert-danger d-flex align-items-center mt-0 mb-2 py-2 px-3"
                              style={{ fontSize: "0.95rem" }}
                            >
                              <MDBIcon
                                icon="exclamation-circle"
                                className="me-2"
                              />
                              <span>{syncMessage}</span>
                            </div>
                          )}
                        </div>
                      );
                    })}

                  {/* Ninja and Jahez Apps with Toggle Switches */}
                  {apps
                    .filter(
                      (app) =>
                        app.store?._id === storeId &&
                        (app.application?.toLowerCase()?.includes("ninja") ||
                          app.application?.toLowerCase()?.includes("jahez"))
                    )
                    .map((app) => {
                      const isToggled = syncToggles[app._id] || false;
                      const isToggling = togglingSync[app._id] || false;

                      return (
                        <div
                          key={app._id}
                          className="d-flex align-items-center justify-content-between gap-3 p-3 border rounded mb-2"
                          style={{ minWidth: "280px" }}
                        >
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-semibold">
                              {app.application}
                            </h6>
                            <div className="d-flex align-items-center gap-2">
                              <small className="text-muted">
                                {isToggled
                                  ? t("Integration Enabled")
                                  : t("Integration Disabled")}
                              </small>
                              {isToggling && (
                                <MDBSpinner
                                  size="sm"
                                  className="text-primary"
                                />
                              )}
                            </div>
                          </div>
                          <div className="d-flex align-items-center justify-content-center">
                            <Form.Check
                              type="switch"
                              id={`sync-switch-${app._id}`}
                              checked={isToggled}
                              onChange={(e) =>
                                handleSyncToggle(app, e.target.checked)
                              }
                              disabled={isToggling}
                              className="sync-toggle-switch mb-0 d-flex align-items-center"
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              </MDBCardBody>
            </MDBCard>
          ) : null}
        </MDBCol>
      </MDBRow>

      <Row>
        <Col>
          <Card className="shadow-sm rounded-4">
            <Card.Header className="bg-light d-flex flex-wrap justify-content-between align-items-center px-3 py-2">
              <h4 className="mb-2 mb-md-0 fw-bold">
                {t("Availability Schedule")}
              </h4>
              {editMode && (
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleOpenEditAvailability}
                >
                  {t("edit")}
                </Button>
              )}
            </Card.Header>
            <MDBRow>
              <MDBCol>
                <MDBCard className="shadow-none">
                  <MDBCardBody className="pb-0">
                    <h6 className="fw-bold mb-4">
                      {t("Availability Sync Status")}
                    </h6>

                    <div className="row g-3">
                      <div className="col-12 col-md-4">
                        <div className="d-flex align-items-start p-3 rounded h-100">
                          <MDBIcon
                            icon="check-circle"
                            className="text-success me-2 mt-1"
                          />
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-semibold">{t("Keeta")}</h6>
                            <p className="mb-0 text-muted small">
                              {t(
                                "Hours are updated immediately when the branch is saved"
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-4">
                        <div className="d-flex align-items-start p-3 rounded h-100">
                          <MDBIcon
                            icon="sync"
                            className="text-primary me-2 mt-1"
                          />
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-semibold">{t("Ninja")}</h6>
                            <p className="mb-0 text-muted small">
                              {t(
                                "Hours will update during the next catalog sync"
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="col-12 col-md-4">
                        <div className="d-flex align-items-start p-3 rounded h-100">
                          <MDBIcon
                            icon="times-circle"
                            className="text-danger me-2 mt-1"
                          />
                          <div className="flex-grow-1">
                            <h6 className="mb-1 fw-semibold">
                              {t("Chefz and Jahez")}
                            </h6>
                            <p className="mb-0 text-muted small">
                              {t("Availability hours not supported")}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </MDBCardBody>
                </MDBCard>
              </MDBCol>
            </MDBRow>
            <Card.Body className="p-3 pt-0">
              {!tempBranch.availableHours ||
              Object.keys(tempBranch.availableHours).length === 0 ? (
                <div className="text-center text-muted py-3">
                  {t("No Schedule Available")}
                </div>
              ) : (
                <ListGroup variant="flush">
                  {Object.entries(tempBranch.availableHours).map(
                    ([day, details]) => (
                      <ListGroup.Item
                        key={day}
                        className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center py-2"
                      >
                        <strong className="text-capitalize">{t(day)}</strong>
                        <div className="mt-2 mt-md-0 d-flex flex-wrap gap-2 justify-content-md-end">
                          {details.is_visible && details.times?.length > 0 ? (
                            details.times.map((t, i) => (
                              <Badge
                                key={i}
                                bg="primary"
                                className="px-2 py-1"
                                style={{ whiteSpace: "nowrap" }}
                              >
                                {t.start} - {t.end}
                              </Badge>
                            ))
                          ) : (
                            <Badge bg="danger">{t("Closed")}</Badge>
                          )}
                        </div>
                      </ListGroup.Item>
                    )
                  )}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Modal
        show={showEditAvailabilityModal}
        onHide={handleScheduleClose}
        size="lg"
        centered
        backdrop="static"
        className="availability-modal p-0"
      >
        <Modal.Header closeButton className="border-0 pb-0">
          <div className="d-flex flex-nowrap w-100 justify-content-between align-items-center">
            <Modal.Title className="h6 fw-bold text-dark d-flex align-items-center mb-0 text-truncate">
              <i className="bi bi-calendar-week me-2"></i>
              {t("Edit Availability Schedule")}
            </Modal.Title>

            {branch?.syncedApps?.some((a) => a.app === "Keeta") && (
              <Button
                color="primary"
                size="sm"
                onClick={fetchKeetaBusinessHours}
                className="d-flex align-items-center flex-shrink-0"
                icon={<RefreshCcw size={16} className={fetchingKeetahours ? "spinner" : ""} />}
              >
                <span className="d-none d-md-inline">
                  {t("Fetch from Keeta")}
                </span>
              </Button>
            )}

            <style jsx>{`
              .spinner {
                animation: spin 1s linear infinite;
              }
              @keyframes spin {
                0% {
                  transform: rotate(0deg);
                }
                100% {
                  transform: rotate(360deg);
                }
              }
            `}</style>
          </div>
        </Modal.Header>
        {/* <Badge className="rounded-0">{t("These are for keeta only")}</Badge> */}
        <Modal.Body
          className="px-4 py-3 overflow-auto"
          style={{ maxHeight: "75vh" }}
        >
          <div className="row g-3">
            {editAvailability &&
              Object.entries(editAvailability).map(([day, details]) => (
                <div key={day} className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body p-3">
                      {/* Day Header */}
                      <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between mb-3">
                        <div className="d-flex align-items-center mb-2 mb-md-0">
                          <div className="day-indicator me-3">
                            <div
                              className={`day-circle ${
                                details.is_visible ? "active" : "inactive"
                              }`}
                            >
                              {t(day.charAt(0))}
                            </div>
                          </div>
                          <div>
                            <h6 className="mb-0 fw-semibold text-dark">
                              {t(day)}
                            </h6>
                            <small className="text-muted">
                              {details.times.length} {t("time slot")}
                              {details.times.length !== 1 ? "s" : ""}
                            </small>
                          </div>
                        </div>

                        <div className="d-flex align-items-center p-0 gap-2">
                          <Form.Check
                            type="switch"
                            id={`switch-${day}`}
                            label={t("Available")}
                            checked={details.is_visible}
                            onChange={(e) =>
                              handleAvailabilityChange(
                                day,
                                "is_visible",
                                e.target.checked
                              )
                            }
                            className="availability-switch"
                          />
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => handleAddTimeRange(day)}
                            className="add-time-btn"
                            disabled={!details.is_visible}
                          >
                            <i className="bi bi-plus-circle me-1"></i>
                            <span className="d-none d-sm-inline">
                              {t("addtime")}
                            </span>
                            <span className="d-sm-none">{t("add")}</span>
                          </Button>
                        </div>
                      </div>

                      {/* Time Slots Grid */}
                      <div
                        className={`${!details.is_visible ? "opacity-50" : ""}`}
                      >
                        <div className="row g-2">
                          {details.times.map((time, idx) => (
                            <div key={idx} className="col-12 p-0">
                              <div className="time-slot-container h-100">
                                <div className="d-flex flex-column bg-light p-2 rounded border border-success-subtle h-100">
                                  <div className="d-flex flex-wrap justify-content-between align-items-end gap-3 w-100 overflow-auto time-row-wrapper">
                                    {/* Time Inputs */}
                                    <div className="d-flex gap-1 flex-wrap time-input-wrapper">
                                      <div className="time-input-group">
                                        <label className="form-label small text-muted mb-1">
                                          {t("Start")}
                                        </label>
                                        <Form.Control
                                          type="time"
                                          value={time.start}
                                          onChange={(e) =>
                                            handleAvailabilityChange(
                                              day,
                                              "time",
                                              {
                                                key: "start",
                                                val: e.target.value,
                                              },
                                              idx
                                            )
                                          }
                                          className=" fixed-time-width m-0"
                                          disabled={!details.is_visible}
                                        />
                                      </div>

                                      <div className="time-input-group">
                                        <label className="form-label small text-muted mb-1">
                                          {t("end")}
                                        </label>
                                        <Form.Control
                                          type="time"
                                          value={time.end}
                                          onChange={(e) =>
                                            handleAvailabilityChange(
                                              day,
                                              "time",
                                              {
                                                key: "end",
                                                val: e.target.value,
                                              },
                                              idx
                                            )
                                          }
                                          className=" fixed-time-width m-0"
                                          disabled={!details.is_visible}
                                        />
                                      </div>
                                    </div>

                                    {/* Delete Button */}
                                    {details.times.length > 1 && (
                                      <div className="text-end flex-shrink-0 delete-btn-container">
                                        <Button
                                          size="sm"
                                          style={{ height: "34px" }}
                                          variant="outline-danger"
                                          onClick={() =>
                                            handleRemoveTimeRange(day, idx)
                                          }
                                          className="remove-btn d-flex align-items-center"
                                          disabled={!details.is_visible}
                                        >
                                          <i className="bi bi-trash3"></i>
                                          <span className="ms-1 remove-text">
                                            {t("remove")}
                                          </span>
                                        </Button>
                                      </div>
                                    )}
                                  </div>

                                  {/* Error Message in a Separate Div */}
                                  {availabilityErrors[`${day}_${idx}`] && (
                                    <div className="mt-1 text-danger small">
                                      {availabilityErrors[`${day}_${idx}`]}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>

                        <style>{`
                .fixed-time-width {
                  width: 140px;
                }

                @media (max-width: 576px) {
                  .fixed-time-width {
                    width: 130px;
                  }
                }

                @media (max-width: 992px) {
                  .remove-text {
                    display: none;
                  }
                  .delete-btn-container .remove-btn {
                    padding: 0.8rem;
                  }
                }

                @media (max-width: 446px) {
                  .time-row-wrapper {
                    flex-direction: column;
                    align-items: stretch;
                  }

                  .time-input-wrapper {
                    width: 100%;
                    gap: 0;
                  }

                  .time-input-group {
                    width: 49%;
                  }

                  .delete-btn-container {
                    width: 100%;
                    margin-top: 8px;
                  }

                  .delete-btn-container .remove-btn {
                    width: 100%;
                    justify-content: center;
                    padding: 0.25rem 0.5rem;
                  }

                  .remove-text {
                    display: inline;
                  }

                  .fixed-time-width {
                    width: 100% !important;
                  }
                }
              `}</style>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0 px-4 pb-4">
          <div className="d-flex gap-2 w-100 justify-content-end">
            <Button
              variant="outline-secondary"
              onClick={handleScheduleClose}
              className="px-4"
            >
              <i className="bi bi-x-circle me-1"></i>
              {t("cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveAvailability}
              className="px-4"
              disabled={
                availabilityLoading ||
                Object.keys(availabilityErrors).length > 0
              }
            >
              {availabilityLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {t("saving")}...
                </>
              ) : (
                <>
                  <i className="bi bi-check-circle me-1"></i>
                  {t("savechanges")}
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>

        <style jsx>{`
          .availability-modal .modal-content {
            border: none;
            border-radius: 16px;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
              0 10px 10px -5px rgba(0, 0, 0, 0.04);
          }

          .availability-modal .modal-header {
            background: #fff;
            border-radius: 16px 16px 0 0;
          }

          .day-indicator {
            position: relative;
          }

          .day-circle {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            font-weight: bold;
            font-size: 16px;
          }

          .day-circle.active {
            background: linear-gradient(135deg, rgb(0, 5, 94), rgb(7, 1, 125));
            color: white;
            box-shadow: 0 4px 12px rgba(40, 167, 69, 0.3);
          }

          .day-circle.inactive {
            background: #f8f9fa;
            color: #6c757d;
            border: 2px solid #dee2e6;
          }

          .add-time-btn {
            border-radius: 8px;
            font-weight: 500;
          }

          .add-time-btn:hover:not(:disabled) {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
          }

          .time-slot-container {
            transition: all 0.3s ease;
          }

          .time-slot-container:hover {
            transform: translateY(-1px);
          }

          .time-input {
            border-radius: 8px;
            border: 1px solid #ced4da;
          }

          .time-input:focus {
            border-color: #007bff;
            box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
          }

          .remove-btn {
            border-radius: 8px;
          }

          .availability-switch .form-check-input:checked {
            background-color: #001f5b;
            border-color: #001f5b;
          }

          @media (max-width: 767.98px) {
            .time-inputs {
              flex-direction: column;
            }
          }
        `}</style>
      </Modal>

      {/* Custom CSS for Sync Toggle Switches */}
      <style jsx>{`
        .sync-toggle-switch {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: auto;
          height: 100%;
        }

        .sync-toggle-switch .form-check-input {
          cursor: pointer;
          margin: 0;
          position: relative;
          top: 50%;
          transform: translateY(-50%);
        }

        .sync-toggle-switch .form-check-input:checked {
          background-color: #001f5b;
          border-color: #001f5b;
        }

        .sync-toggle-switch .form-check-input:focus {
          box-shadow: 0 0 0 0.2rem rgba(0, 31, 91, 0.25);
        }

        .sync-toggle-switch .form-check-input:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
      `}</style>

      {renderTimeSlotTables()}
    </MDBContainer>
  );
};

export default ViewBranchPage;